/**
 * transport/wsClient.ts —— **WS 单入口客户端**（U-921；唯一实装通道）。
 * 契约：CONTRACT §1–§6 —— 建连/重连/退避/错误面＋事件游标＋断线回放。
 * 纪律：出网只经 whitelist 白名单（/stream）；帧只经 parseFrame；重复事件 id 去重（§5 重连幂等）。
 * 心跳口径：连接级 ping/pong（浏览器端＝自动回 pong；**协议层帧 JS 不可见**）；客户端侧——
 *      **不做任何心跳/静默判死**（后端 `/stream` 设计＝**只握手/心跳、无帧**，WS 协议层 ping/pong JS 不可见
 *      ⇒ 「等首帧」类判据恒误报，20261011 已整链删除）；连接真断由 `onclose/onerror` ⇒ 退避重连兜底
 *      走退避重连（间隔默认 30s，§5 待标定）。**静默 ≠ 死**（后台节流/空闲期本就无下行帧）
 *      ⇒ 20261011 起**不判死链、不出链路类提示**（原「建连期首帧哨」→ 已整链退役）；活性恢复
 *      仍由「onclose/onerror ⇒ 退避重连」兜底（连接真断必有事件，不靠无帧推断）。
 */
import { backoffDelayMs, DEFAULT_BACKOFF, type BackoffOptions } from "./backoff";
import { EventCursor } from "./cursor";
import type { Frame } from "./dto";
import { E_CODES, TransportError } from "./errors";
import { parseFrame } from "./frame";
import { wsUrl } from "./whitelist";

export interface WebSocketLike {
  readyState: number;
  send(data: string): void;
  close(): void;
  onopen: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onerror: ((ev: unknown) => void) | null;
  onclose: ((ev: unknown) => void) | null;
}

export type WsFactory = (url: string) => WebSocketLike;
export type TransportStatus = "idle" | "connecting" | "open" | "reconnecting" | "closed";

export interface WsClientOptions {
  origin: { protocol: string; host: string };
  onFrame: (frame: Frame) => void;
  onStatus: (status: TransportStatus) => void;
  onError: (err: TransportError) => void;
  wsFactory?: WsFactory;
  /**
   * 当前**会话容器**取值器（★C-Ⅷ）：建连时取一次随 URL 上报（`?session=`）。
   * 用取值器而非值 ⇒ 重连（退避后）自动带上当时的会话；切会话由宿主触发重连。
   */
  session?: () => string | null;
  backoff?: BackoffOptions;
  rand?: () => number;
  now?: () => number;
}


export class WsClient {
  private readonly opts: WsClientOptions;
  private readonly cursorStore = new EventCursor();
  private socket: WebSocketLike | null = null;
  private attempt = 0;
  private stopped = true;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(opts: WsClientOptions) {
    this.opts = opts;
  }

  /** 启动：建连（首连 status=connecting；重连=reconnecting） */
  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    this.attempt = 0;
    this.connect();
  }

  /** 停止：断连并清定时器（不再重连） */
  stop(): void {
    this.stopped = true;
    this.clearTimers();
    this.detach();
    this.opts.onStatus("closed");
  }

  /** 上行命令（同一 WS 连接，CONTRACT §2 双向）；未连 ⇒ false（不缓存假发） */
  send(payload: unknown): boolean {
    if (!this.socket || this.socket.readyState !== 1) return false;
    this.socket.send(JSON.stringify(payload));
    return true;
  }

  /** 当前游标（断线回放起点；等价 Last-Event-ID） */
  cursor(): string | null {
    return this.cursorStore.value();
  }

  private connect(): void {
    const { origin, onStatus, onError } = this.opts;
    onStatus(this.attempt === 0 ? "connecting" : "reconnecting");
    let socket: WebSocketLike;
    try {
      const factory = this.opts.wsFactory ?? defaultFactory;
      socket = factory(wsUrl(origin, this.cursorStore.value(),
        this.opts.session ? this.opts.session() : null));
    } catch (err) {
      onError(connectFailed(err));
      this.scheduleReconnect();
      return;
    }
    this.socket = socket;
    socket.onopen = () => this.handleOpen();
    socket.onmessage = (ev) => this.handleMessage(ev);
    socket.onerror = (ev) => this.handleError(ev);
    socket.onclose = () => this.handleClose();
  }

  private handleOpen(): void {
    this.attempt = 0;
    this.opts.onStatus("open");
  }

  private handleMessage(ev: { data: unknown }): void {
    if (typeof ev.data !== "string") {
      this.opts.onError(parseFailure("帧数据非文本"));
      return;
    }
    let frame: Frame;
    try {
      frame = parseFrame(ev.data);
    } catch (err) {
      this.opts.onError(err instanceof TransportError ? err : parseFailure(String(err)));
      return;
    }
    if ("views" in frame) {
      this.cursorStore.adopt(frame.cursor);
    } else if (!this.cursorStore.observe(frame.id)) {
      return; // 重连幂等：重复事件 id 去重
    }
    this.opts.onFrame(frame);
  }

  private handleError(ev: unknown): void {
    this.opts.onError(connectFailed(ev));
  }

  private handleClose(): void {
    if (this.stopped) return;
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    if (this.stopped) return;
    this.clearTimers();
    this.detach();
    const opts = this.opts.backoff ?? DEFAULT_BACKOFF;
    const delay = backoffDelayMs(this.attempt, opts, this.opts.rand ?? Math.random);
    this.attempt += 1;
    this.opts.onStatus("reconnecting");
    this.reconnectTimer = setTimeout(() => this.connect(), delay);
  }

  private detach(): void {
    if (!this.socket) return;
    this.socket.onopen = null;
    this.socket.onmessage = null;
    this.socket.onerror = null;
    this.socket.onclose = null;
    try {
      this.socket.close();
    } catch {
      /* close 幂等；异常不改变状态语义 */
    }
    this.socket = null;
  }


  private clearTimers(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private now(): number {
    return (this.opts.now ?? Date.now)();
  }
}

function defaultFactory(url: string): WebSocketLike {
  return new WebSocket(url) as unknown as WebSocketLike;
}

function connectFailed(detail: unknown): TransportError {
  // 提示文案＝errorText 按码分流（本处只给事实位 message；链路类码的提示在文案源内）
  return new TransportError(
    E_CODES.WS_CONNECT_FAILED, "connect",
    "WS 建连/链路失败：" + String(detail),
  );
}

function parseFailure(detail: string): TransportError {
  return new TransportError(
    E_CODES.FRAME_INVALID, "decode", "下行帧解析失败：" + detail,
  );
}
